import React, { useRef } from "react";
import styles from "./FileInput.module.scss";
import {
  Controller,
  type Control,
  type RegisterOptions,
  type UseFormRegister,
} from "react-hook-form";
import withLabelAndError from "../withLabelAndError";
import toast from "@/lib/toast";

interface FileInputProps {
  control?: Control<any>;
  register?: UseFormRegister<any>;
  validations?: RegisterOptions;
  name: string;
  placeholder?: string;
  [key: string]: any; // to allow ...rest props
}

const FileInput: React.FC<FileInputProps> = ({
  control,
  register,
  validations,
  name,
  placeholder = "Select files...",
  ...rest
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const renderInput = (field: {
    value: File[];
    onChange: (value: File[]) => void;
  }) => {
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      const accept = e.target.accept
        ?.split(",")
        .map((a) => a.trim().toLowerCase());

      const validFiles: File[] = [];
      const invalidFiles: string[] = [];

      const getFileExtension = (fileName: string): string =>
        "." + fileName.split(".").pop()?.toLowerCase();

      const isFileAccepted = (
        fileExt: string,
        mimeType: string,
        accept: string[] = []
      ): boolean => {
        return accept.some((rule) => {
          if (rule.startsWith(".")) {
            return fileExt === rule;
          }

          if (rule.endsWith("/*")) {
            const typeCategory = rule.split("/")[0];
            return mimeType.startsWith(typeCategory + "/");
          }

          return mimeType === rule;
        });
      };

      files.forEach((file) => {
        const fileExt = getFileExtension(file.name);
        const mimeType = file.type.toLowerCase();

        const isValid = isFileAccepted(fileExt, mimeType, accept);

        if (isValid) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      });

      if (invalidFiles.length > 0) {
        toast.error(`These files are not allowed: ${invalidFiles.join(", ")}`);
      }

      field.onChange(validFiles);
    };

    const handleRemoveFile = (index: number) => {
      const newFiles = field.value.filter((_, i) => i !== index);
      field.onChange(newFiles);
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    };

    return (
      <div className={styles.fileWrapper}>
        <input
          {...rest}
          type="file"
          id={name}
          name={name}
          ref={inputRef}
          className={styles.hiddenFileInput}
          onChange={handleFileChange}
        />

        <div className={`${styles.inputField} ${styles.chipInput}`}>
          {field.value && field.value.length > 0 ? (
            field.value.map((file, index) => (
              <abbr title={file.name} key={index + 1}>
                <span className={styles.fileChip}>
                  <p>{file.name}</p>
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(index)}
                    className={styles.removeChip}
                  >
                    &times;
                  </button>
                </span>
              </abbr>
            ))
          ) : (
            <span className={styles.placeholder}>{placeholder}</span>
          )}
        </div>
        <label htmlFor={name} className={styles.fileButton}>
          Select Files
        </label>
      </div>
    );
  };

  if (!register) {
    return (
      control && (
        <Controller
          control={control}
          name={name}
          rules={validations ?? {}}
          render={({ field }) => renderInput(field)}
        />
      )
    );
  } else {
    const field = register(name, {
      ...validations,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        validations?.onChange?.(e) ?? Array.from(e.target.files || []),
    }) as any;

    return renderInput(field);
  }
};

const FileInputWithLabelAndError = withLabelAndError(FileInput);

export default FileInputWithLabelAndError;
export { FileInput };
